import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
// import "antd/dist/reset.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

import AppLayout from "./layouts/AppLayout";
import AppRoutes from "./routes";
import { queryClient } from "./lib/queryClient";
import { queryStorage } from "./lib/queryStorage";
import { useAuthStore } from "./stores/useAuthStore";
import {
  shouldForceSignOut,
  subscribeToSessionChangeElsewhere,
} from "./lib/sessionBroadcast";
import { usePostHogPageview } from "./hooks/usePostHogPageview";
import { usePostHogIdentity } from "./hooks/usePostHogIdentity";

/* -------------------------
   IndexedDB Persister
-------------------------- */
const persister = createAsyncStoragePersister({
  storage: queryStorage,
});

/* -------------------------
   Enable Persistence
-------------------------- */
// `buster` invalidates every persisted cache entry the moment it changes —
// bump it whenever a cached API response's shape changes (renamed/removed
// fields, etc.), otherwise old IndexedDB entries can rehydrate with a shape
// current code doesn't expect and crash on render before a fresh fetch can
// correct them. Bumped for the Telemetry Outage backend integration, which
// changed causeAtDetection from an object to a raw string and removed
// recoveryStatus.
persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  buster: "2026-08-05-telemetry-start-date-refactor",
});

const App: React.FC = () => {
  const { pathname } = useLocation();
  const isAuthRoute =
    pathname.startsWith("/invite/") ||
    pathname === "/sign-in" ||
    pathname === "/forgot-password" ||
    pathname === "/reset-password";

  usePostHogPageview();
  usePostHogIdentity();

  useEffect(() => {
    return subscribeToSessionChangeElsewhere((reason) => {
      const myEmail = useAuthStore.getState().user?.email ?? null;

      // A logout always forces this tab out. A sign-in only does when it's
      // a genuinely different identity — the same person signing in again
      // in another tab isn't a security event for this tab (see
      // shouldForceSignOut for the full reasoning).
      if (!shouldForceSignOut(reason, myEmail)) return;

      useAuthStore.getState().clearAuth();
      queryClient.clear();
      if (window.location.pathname !== "/sign-in") {
        window.location.assign("/sign-in");
      }
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {isAuthRoute ? (
        <AppRoutes />
      ) : (
        <AppLayout>
          <AppRoutes />
        </AppLayout>
      )}
    </QueryClientProvider>
  );
};

export default App;
